import type { Locale } from '../i18n/locale';
import { PREFILL_KEY } from './site';
import { ROUTES } from '../i18n/locale';

/**
 * Where the buyer is in the purchasing decision — shared by the HOME project brief and the intake, so
 * the choice made on the HOME arrives in the intake unchanged.
 */
export const START_IDS = ['need', 'specification', 'supplier'] as const;
export type StartId = (typeof START_IDS)[number];

export const START: Record<Locale, { label: string; summary: string; options: Record<StartId, { title: string; body: string; hint: string }> }> = {
  'es-CO': {
    label: '¿Dónde está hoy su compra?',
    summary: 'Punto de partida',
    options: {
      need: { title: 'Tengo una necesidad', body: 'Todavía estamos definiendo qué comprar.', hint: '¿Qué necesita resolver o abastecer?' },
      specification: { title: 'Tengo especificación', body: 'Ya contamos con referencia, ficha técnica, BOM o alcance.', hint: 'Puede describir la referencia, modelo, BOM o especificación.' },
      supplier: { title: 'Tengo proveedor o cotización', body: 'Queremos validar, comparar o estructurar la operación.', hint: 'Puede indicar proveedor, origen, precio o condiciones conocidas.' },
    },
  },
  en: {
    label: 'Where is your purchase today?',
    summary: 'Starting point',
    options: {
      need: { title: 'I have a need', body: 'We are still defining what to purchase.', hint: 'What do you need to solve or supply?' },
      specification: { title: 'I have a specification', body: 'We already have a reference, technical sheet, BOM or scope.', hint: 'You can describe the reference, model, BOM or specification.' },
      supplier: { title: 'I have a supplier or quotation', body: 'We want to validate, compare or structure the operation.', hint: 'You can share the supplier, origin, price or known terms.' },
    },
  },
};
export const isStartId = (value: unknown): value is StartId => (START_IDS as readonly unknown[]).includes(value);

/** Hand-off from the HOME to the intake (same locale): text and starting point travel in session storage, never in the URL. */
export interface Prefill { need: string; start: StartId | '' }
export function startIntake(locale: Locale, prefill: Prefill) {
  try {
    if (prefill.need.trim() || prefill.start) sessionStorage.setItem(PREFILL_KEY, JSON.stringify({ need: prefill.need.trim().slice(0, 2000), start: prefill.start }));
  } catch { /* storage unavailable: the intake starts empty */ }
  window.location.href = ROUTES.intake[locale];
}
/** Reads (and clears) the hand-off; accepts the earlier plain-text format. */
export function takePrefill(): Prefill | null {
  try {
    const raw = sessionStorage.getItem(PREFILL_KEY); sessionStorage.removeItem(PREFILL_KEY); if (!raw) return null;
    try { const parsed = JSON.parse(raw) as Partial<Prefill>; return { need: typeof parsed.need === 'string' ? parsed.need : '', start: isStartId(parsed.start) ? parsed.start : '' }; }
    catch { return { need: raw, start: '' }; }
  } catch { return null; }
}
